import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getEdgeSettingsPaths } from '@app/utils/appContext';
import './Repositories.css';
import {
  Button,
  Checkbox,
  Dropdown,
  DropdownItem,
  DropdownList,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Flex,
  Icon,
  MenuToggle,
  MenuToggleElement,
  PageSection,
  Pagination,
  SearchInput,
  Select,
  SelectOption,
  Stack,
  StackItem,
  Title,
  Content,
  Toolbar,
  ToolbarContent,
  ToolbarGroup,
  ToolbarItem,
} from '@patternfly/react-core';
import { PageContextActions, PageHeaderTitle } from '@app/components/PageContextActions';
import { StatusLabel } from '@app/components/StatusLabel';
// Using DataList instead of Table for PatternFly 6 compatibility
import {
  EllipsisVIcon,
  PlusIcon,
  RepositoryIcon,
} from '@patternfly/react-icons';
import { addRepository, mockRepositories } from '../data/repositoryData';
import { Repository, RepositoryFormData } from '../types/repository';
import { formatDate } from '../utils/dateFormatter';

const Repositories: React.FunctionComponent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const navigateToCreateRepository = () => {
    navigate(`${getEdgeSettingsPaths(location.pathname).repositories}/create`);
  };
  const [repositories, setRepositories] = React.useState<Repository[]>(mockRepositories);
  const [filteredRepositories, setFilteredRepositories] = React.useState<Repository[]>(mockRepositories);
  const [searchValue, setSearchValue] = React.useState('');
  const [isFilterOpen, setIsFilterOpen] = React.useState(false);
  const [selectedFilter, setSelectedFilter] = React.useState('All repositories');
  const [openDropdowns, setOpenDropdowns] = React.useState<Record<string, boolean>>({});
  const [selectedRepositories, setSelectedRepositories] = React.useState<Set<string>>(new Set());
  
  // Pagination state
  const [page, setPage] = React.useState(1);
  const [perPage, setPerPage] = React.useState(10);

  // Sync repositories with mockRepositories when component mounts or location changes
  React.useEffect(() => {
    setRepositories([...mockRepositories]);
  }, [location.pathname]);

  // Handle search
  React.useEffect(() => {
    if (!searchValue) {
      setFilteredRepositories(repositories);
    } else {
      const filtered = repositories.filter(repo =>
        repo.name.toLowerCase().includes(searchValue.toLowerCase()) ||
        repo.url.toLowerCase().includes(searchValue.toLowerCase())
      );
      setFilteredRepositories(filtered);
    }
    setPage(1); // Reset to first page when searching
  }, [repositories, searchValue]);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
  };

  const handleCreateRepository = (formData: RepositoryFormData) => {
    const newRepo = addRepository({
      ...formData,
      syncStatus: 'pending',
      lastTransition: new Date().toISOString()
    });
    setRepositories([...repositories, newRepo]);
  };

  const getSyncStatusBadge = (status: Repository['syncStatus']) => {
    if (status === 'Accessible') {
      return (
        <StatusLabel status="success">
          {status}
        </StatusLabel>
      );
    }
    if (status === 'No access') {
      return (
        <StatusLabel status="danger">
          {status}
        </StatusLabel>
      );
    }

    const tones = {
      successful: 'success',
      failed: 'danger',
      pending: 'pending',
      never: 'unknown',
    } as const;

    const labels = {
      successful: 'Successful',
      failed: 'Failed',
      pending: 'Pending',
      never: 'Never synced',
    };

    return (
      <StatusLabel status={tones[status as keyof typeof tones]}>
        {labels[status as keyof typeof labels]}
      </StatusLabel>
    );
  };

  const onRepoDropdownToggle = (repoId: string) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [repoId]: !prev[repoId]
    }));
  };

  const onRepoDropdownSelect = (repoId: string, action?: string) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [repoId]: false
    }));
    
    if (action === 'edit') {
      navigate(`${getEdgeSettingsPaths(location.pathname).repositories}/edit/${repoId}`);
    }
  };

  const onFilterToggle = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  const onFilterSelect = (_event: React.MouseEvent<Element, MouseEvent> | undefined, value: string | number | undefined) => {
    setSelectedFilter(value as string);
    setIsFilterOpen(false);
  };

  const filterToggle = (toggleRef: React.Ref<MenuToggleElement>) => (
    <MenuToggle
      ref={toggleRef}
      onClick={onFilterToggle}
      isExpanded={isFilterOpen}
      style={{ width: '100%', maxWidth: '200px' }}
    >
      {selectedFilter}
    </MenuToggle>
  );

  const formatLastTransition = (timestamp: string) => {
    // If it's already a relative time string like "X hours ago", return it as is
    if (typeof timestamp === 'string' && timestamp.includes('ago')) {
      return timestamp;
    }
    // Otherwise, format it as "Month DD, YYYY"
    return formatDate(timestamp);
  };

  // Pagination
  const startIndex = (page - 1) * perPage;
  const endIndex = startIndex + perPage;
  const paginatedRepositories = filteredRepositories.slice(startIndex, endIndex);

  // Handle repository selection
  const handleSelectRepository = (repoId: string, checked: boolean) => {
    setSelectedRepositories(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(repoId);
      } else {
        newSet.delete(repoId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRepositories(new Set(paginatedRepositories.map(r => r.id)));
    } else {
      setSelectedRepositories(new Set());
    }
  };

  const isAllSelected = selectedRepositories.size === paginatedRepositories.length && paginatedRepositories.length > 0;
  const isPartiallySelected = selectedRepositories.size > 0 && selectedRepositories.size < paginatedRepositories.length;

  const onSetPage = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPage: number) => {
    setPage(newPage);
  };

  const onPerPageSelect = (
    _event: React.MouseEvent | React.KeyboardEvent | MouseEvent,
    newPerPage: number,
    newPage: number
  ) => {
    setPerPage(newPerPage);
    setPage(newPage);
  };

  // Empty state when no repositories
  if (filteredRepositories.length === 0 && !searchValue) {
    return (
      <>
        {/* Page Header with Toolbar - Single Line */}
        <PageSection hasBodyWrapper={false} style={{ paddingBottom: 0 }}>
          <div style={{ paddingLeft: 'var(--acm-page-inline-x, var(--pf-t--global--spacer--md))', paddingRight: 'var(--acm-page-inline-x, var(--pf-t--global--spacer--md))' }}>
          <Flex 
            justifyContent={{ default: 'justifyContentSpaceBetween' }}
            alignItems={{ default: 'alignItemsCenter' }}
              style={{ marginBottom: 'var(--pf-t--global--spacer--md)' }}
          >
            <PageHeaderTitle
              title="Repositories"
              description="Manage the repositories used for your application deployments."
            />
            <PageContextActions placement="toolbar" />
          </Flex>
          </div>
        </PageSection>

        {/* Search Toolbar */}
        <PageSection hasBodyWrapper={false}>
          <Stack hasGutter>
            <StackItem>
              <Toolbar>
                <ToolbarContent>
                  <ToolbarGroup align={{ default: 'alignStart' }}>
                  <ToolbarItem>
                    <SearchInput
                        className="app-search-input"
                        placeholder="Search"
                      value={searchValue}
                      onChange={(_, value) => handleSearchChange(value)}
                      onClear={() => handleSearchChange('')}
                    />
                  </ToolbarItem>
                  <ToolbarItem>
                    <Button
                      variant="primary"
                        onClick={navigateToCreateRepository}
                    >
                      Create a repository
                    </Button>
                  </ToolbarItem>
                  </ToolbarGroup>
                </ToolbarContent>
              </Toolbar>
            </StackItem>

            <StackItem isFilled>
              <EmptyState variant="lg">
                <Icon size="xl">
                  <RepositoryIcon />
                </Icon>
                <Title headingLevel="h4" size="lg">
                  No repositories here!
                </Title>
                <EmptyStateBody>
                  You can create repositories and use them to point to GitHub repositories.
                  <br />
                  Adding them resource syncs will allow you to keep your fleet&apos;s configurations updated
                  <br />
                  and synced automatically.
                </EmptyStateBody>
                <EmptyStateFooter>
                  <EmptyStateActions>
                    <Button
                      variant="primary"
                      onClick={navigateToCreateRepository}
                    >
                      Create a repository
                    </Button>
                  </EmptyStateActions>
                </EmptyStateFooter>
              </EmptyState>
            </StackItem>
          </Stack>
        </PageSection>

      </>
    );
  }

  return (
    <>
      {/* Page Header with Toolbar - Single Line */}
      <PageSection hasBodyWrapper={false} style={{ paddingBottom: 0 }}>
        <div style={{ paddingLeft: 'var(--acm-page-inline-x, var(--pf-t--global--spacer--md))', paddingRight: 'var(--acm-page-inline-x, var(--pf-t--global--spacer--md))' }}>
        <Flex 
          justifyContent={{ default: 'justifyContentSpaceBetween' }}
          alignItems={{ default: 'alignItemsCenter' }}
            style={{ marginBottom: 'var(--pf-t--global--spacer--md)' }}
        >
          <PageHeaderTitle
            title="Repositories"
            description="Manage the repositories used for your application deployments."
          />
          <PageContextActions placement="toolbar" />
        </Flex>
        </div>
      </PageSection>

      {/* Search Toolbar */}
      <PageSection hasBodyWrapper={false}>
        <div style={{ paddingLeft: 'var(--acm-page-inline-x, var(--pf-t--global--spacer--md))', paddingRight: 'var(--acm-page-inline-x, var(--pf-t--global--spacer--md))' }}>
        <Stack hasGutter>
          <StackItem>
            <Toolbar>
              <ToolbarContent>
                {selectedRepositories.size > 0 ? (
                  <ToolbarGroup align={{ default: 'alignStart' }}>
                    <ToolbarItem>
                      <Content component="p" style={{ margin: 0, marginRight: 'var(--pf-t--global--spacer--md)' }}>
                        {selectedRepositories.size} selected
                      </Content>
                    </ToolbarItem>
                    <ToolbarItem>
                      <Button variant="danger" onClick={() => setSelectedRepositories(new Set())}>
                        Delete
                      </Button>
                    </ToolbarItem>
                  </ToolbarGroup>
                ) : (
                  <ToolbarGroup align={{ default: 'alignStart' }}>
                      <ToolbarItem>
                        <Select
                          toggle={filterToggle}
                          onSelect={onFilterSelect}
                          selected={selectedFilter}
                          isOpen={isFilterOpen}
                          onOpenChange={(isOpen) => setIsFilterOpen(isOpen)}
                        >
                          <SelectOption value="All repositories">All repositories</SelectOption>
                          <SelectOption value="Accessible">Accessible</SelectOption>
                          <SelectOption value="No access">No access</SelectOption>
                          <SelectOption value="Successful">Successful</SelectOption>
                          <SelectOption value="Failed">Failed</SelectOption>
                        </Select>
                      </ToolbarItem>
                <ToolbarItem>
                  <SearchInput
                    className="app-search-input"
                    placeholder="Search"
                    value={searchValue}
                    onChange={(_, value) => handleSearchChange(value)}
                    onClear={() => handleSearchChange('')}
                  />
                </ToolbarItem>
                <ToolbarItem>
                  <Button
                    variant="primary"
                          onClick={navigateToCreateRepository}
                  >
                    Create a repository
                  </Button>
                </ToolbarItem>
                    </ToolbarGroup>
                )}
              </ToolbarContent>
            </Toolbar>
          </StackItem>

          {filteredRepositories.length === 0 ? (
            <StackItem isFilled>
              <EmptyState variant="sm">
                <Icon size="lg">
                  <RepositoryIcon />
                </Icon>
                <Title headingLevel="h4" size="lg">
                  No matching repositories found
                </Title>
                <EmptyStateBody>
                  No repositories match your search criteria. Try adjusting your search terms.
                </EmptyStateBody>
              </EmptyState>
            </StackItem>
          ) : (
            <>
              <StackItem isFilled>
                {/* Repositories Table */}
                <div className="app-data-table app-data-table--visible-overflow" style={{ position: 'relative', paddingLeft: 'var(--acm-page-inline-x, var(--pf-t--global--spacer--md))', paddingRight: 'var(--acm-page-inline-x, var(--pf-t--global--spacer--md))' }}>
                  {/* Table Header */}
                <div className="app-data-table__header" style={{ 
                    display: 'grid',
                    gridTemplateColumns: '0.3fr 2fr 1.5fr 2.5fr 1.5fr 1.5fr 0.5fr',
                    padding: 'var(--pf-t--global--spacer--sm) var(--pf-t--global--spacer--md)',
                  backgroundColor: 'var(--pf-t--global--color--nonstatus--gray--50)',
                    borderBottom: '1px solid var(--pf-t--global--color--nonstatus--gray--200)',
                  fontWeight: 'bold',
                    fontSize: 'var(--pf-t--global--font--size--sm)',
                    alignItems: 'center',
                }}>
                    <div>
                      <Checkbox
                        id="select-all-repositories"
                        isChecked={isAllSelected ? true : isPartiallySelected ? null : false}
                        onChange={(_, checked) => handleSelectAll(checked)}
                        aria-label="Select all repositories"
                      />
                    </div>
                    <div>Name</div>
                    <div>Type</div>
                    <div>URL</div>
                    <div>Sync status</div>
                    <div>Last transition</div>
                    <div></div>
                </div>
                
                  {/* Table Rows */}
                  {paginatedRepositories.map((repo, index) => (
                    <div
                      key={repo.id}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '0.3fr 2fr 1.5fr 2.5fr 1.5fr 1.5fr 0.5fr',
                        padding: 'var(--pf-t--global--spacer--sm) var(--pf-t--global--spacer--md)',
                        borderBottom: index < paginatedRepositories.length - 1 
                          ? '1px solid var(--pf-t--global--color--nonstatus--gray--200)' 
                          : 'none',
                        alignItems: 'center',
                      }}
                    >
                      <div>
                        <Checkbox
                          id={`select-repository-${repo.id}`}
                          isChecked={selectedRepositories.has(repo.id)}
                          onChange={(_, checked) => handleSelectRepository(repo.id, checked)}
                          aria-label={`Select ${repo.name}`}
                        />
                      </div>
                      <div>
                        <a
                          href="#"
                          onClick={(e) => e.preventDefault()}
                                style={{ 
                                  color: 'var(--pf-t--global--color--brand--default)',
                            textDecoration: 'none',
                                }}
                              >
                          {repo.name}
                              </a>
                      </div>
                      <div>{repo.type || 'N/A'}</div>
                      <div style={{ wordBreak: 'break-word' }}>{repo.url}</div>
                      <div>
                              {getSyncStatusBadge(repo.syncStatus)}
                      </div>
                      <div>{typeof repo.lastTransition === 'string' && repo.lastTransition.includes('ago') ? repo.lastTransition : formatLastTransition(repo.lastTransition)}</div>
                      <div>
                        <Dropdown
                          isOpen={openDropdowns[repo.id] || false}
                          onSelect={() => onRepoDropdownSelect(repo.id)}
                          onOpenChange={(isOpen: boolean) => {
                            setOpenDropdowns(prev => ({
                              ...prev,
                              [repo.id]: isOpen
                            }));
                          }}
                          toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                            <MenuToggle
                              ref={toggleRef}
                              onClick={() => onRepoDropdownToggle(repo.id)}
                              isExpanded={openDropdowns[repo.id] || false}
                              variant="plain"
                              icon={<EllipsisVIcon />}
                              aria-label={`Actions for ${repo.name}`}
                            />
                          )}
                          popperProps={{
                            appendTo: () => document.body,
                            enableFlip: true,
                            position: 'end',
                            minWidth: '150px',
                            maxWidth: '200px',
                          }}
                        >
                          <DropdownList>
                            <DropdownItem
                              key="view-details"
                              onClick={() => onRepoDropdownSelect(repo.id)}
                            >
                              View
                            </DropdownItem>
                            <DropdownItem
                              key="edit"
                              onClick={() => onRepoDropdownSelect(repo.id, 'edit')}
                            >
                              Edit
                            </DropdownItem>
                          </DropdownList>
                        </Dropdown>
                      </div>
                    </div>
                  ))}
                </div>
              </StackItem>

              <StackItem>
                <div style={{ paddingRight: 'var(--acm-page-inline-x, var(--pf-t--global--spacer--md))' }}>
                <Pagination
                  itemCount={filteredRepositories.length}
                  perPage={perPage}
                  page={page}
                  onSetPage={onSetPage}
                  widgetId="repositories-pagination"
                  onPerPageSelect={onPerPageSelect}
                  isCompact
                />
                </div>
              </StackItem>
            </>
          )}
        </Stack>
        </div>
      </PageSection>

    </>
  );
};

export { Repositories };
